import { useEffect, useState } from 'react';

function SectionToggle({ label, expanded, onToggle }) {
  return (
    <button type="button" className="overview-section-toggle" aria-expanded={expanded} onClick={onToggle}>
      <span>{label}</span>
      <span>{expanded ? '▾' : '▸'}</span>
    </button>
  );
}

function ControlCenterOverview({
  boards = [],
  metrics = [],
  operations = [],
  performance = [],
  onSelectBoard = () => {},
  title = 'Tableros rapidos',
  description = 'Facturacion y reportes en un solo panel.',
  compactMode = false
}) {
  const hasMetrics = Boolean(metrics.length);
  const hasOperations = Boolean(operations.length);
  const hasPerformance = Boolean(performance.length);
  const hasBoards = Boolean(boards.length);

  const [showOperations, setShowOperations] = useState(() => !compactMode);
  const [showPerformance, setShowPerformance] = useState(() => !compactMode);
  const [showBoards, setShowBoards] = useState(() => !compactMode);

  useEffect(() => {
    if (!compactMode) {
      setShowOperations(true);
      setShowPerformance(true);
      setShowBoards(true);
    }
  }, [compactMode]);

  return (
    <section className="control-overview">
      <header className="control-header overview-animate">
        <p className="eyebrow">Sistema de control interno</p>
        <h2>{title}</h2>
        <p>{description}</p>
      </header>

      {hasMetrics && (
        <section className="overview-metrics-grid">
          {metrics.map((metric, index) => (
            <article
              key={metric.key || metric.label}
              className={`overview-metric-card tone-${metric.tone || 'neutral'} overview-animate`}
              style={{ '--stagger-delay': `${index * 70}ms` }}
            >
              <p className="label">{metric.label}</p>
              <strong>{metric.value}</strong>
              {metric.detail && <span>{metric.detail}</span>}
              {metric.delta && <small>{metric.delta}</small>}
            </article>
          ))}
        </section>
      )}

      {hasOperations && compactMode && (
        <SectionToggle
          label="Semaforo operativo"
          expanded={showOperations}
          onToggle={() => setShowOperations(prev => !prev)}
        />
      )}
      {hasOperations && showOperations && (
        <section className="overview-table-panel overview-animate">
          <div className="overview-panel-header">
            <div>
              <h3>Semaforo operativo</h3>
              <p>Monitorea cargas de trabajo y abre cada modulo desde esta vista.</p>
            </div>
          </div>
          <div className="overview-table-wrap">
            <table className="overview-table">
              <thead>
                <tr>
                  <th>Modulo</th>
                  <th>Pendientes</th>
                  <th>Rendimiento</th>
                  <th>Siguiente accion</th>
                  <th>Atajo</th>
                </tr>
              </thead>
              <tbody>
                {operations.map(operation => (
                  <tr key={operation.key || operation.module}>
                    <td>
                      <div className="overview-module-cell">
                        <span className={`status-dot ${operation.tone || 'neutral'}`} />
                        <div>
                          <strong>{operation.module}</strong>
                          {operation.focus && <small>{operation.focus}</small>}
                        </div>
                      </div>
                    </td>
                    <td>{operation.pending}</td>
                    <td>{operation.performance}</td>
                    <td>{operation.nextAction}</td>
                    <td>
                      <button
                        type="button"
                        className="btn ghost compact"
                        disabled={!operation.boardKey}
                        onClick={() => operation.boardKey && onSelectBoard(operation.boardKey)}
                      >
                        {operation.actionLabel || 'Abrir'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {hasPerformance && compactMode && (
        <SectionToggle
          label="Ritmo del despacho"
          expanded={showPerformance}
          onToggle={() => setShowPerformance(prev => !prev)}
        />
      )}
      {hasPerformance && showPerformance && (
        <section className="overview-performance-panel overview-animate">
          <div className="overview-panel-header">
            <div>
              <h3>Ritmo del despacho</h3>
              <p>Avance de cada frente operativo para detectar cuellos de botella.</p>
            </div>
          </div>
          <div className="overview-performance-list">
            {performance.map((item, index) => (
              <article
                key={item.key || item.label}
                className="overview-performance-item"
                style={{ '--stagger-delay': `${index * 80}ms` }}
              >
                <div className="overview-performance-head">
                  <strong>{item.label}</strong>
                  <span>{item.percent}%</span>
                </div>
                {item.detail && <p>{item.detail}</p>}
                <div className="overview-progress-track">
                  <span
                    className={`overview-progress-fill tone-${item.tone || 'neutral'}`}
                    style={{ '--progress-width': `${item.percent}%` }}
                  />
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {(hasBoards || !boards.length) && compactMode && (
        <SectionToggle
          label="Accesos directos"
          expanded={showBoards}
          onToggle={() => setShowBoards(prev => !prev)}
        />
      )}
      {showBoards && (
        <section className="overview-board-panel overview-animate">
          <div className="overview-panel-header">
            <div>
              <h3>Accesos directos por modulo</h3>
              <p>Ingresa rapido a la herramienta que necesitas para continuar.</p>
            </div>
          </div>
          <div className="control-board-grid">
            {!boards.length && (
              <article className="control-board-card disabled">
                <div className="control-board-body">
                  <div>
                    <h3>Sin modulos disponibles</h3>
                    <p>No tienes permisos activos para abrir tableros en este momento.</p>
                  </div>
                </div>
              </article>
            )}
            {boards.map(board => (
              <article key={board.key} className={`control-board-card ${board.disabled ? 'disabled' : ''}`}>
                <div className="control-board-body">
                  <div>
                    <h3>{board.title}</h3>
                    <p>{board.description}</p>
                  </div>
                  <ul>
                    {board.highlights.map(highlight => (
                      <li key={highlight.label}>
                        <span>{highlight.label}</span>
                        <strong>{highlight.value}</strong>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="control-board-actions">
                  <button
                    type="button"
                    className="btn primary"
                    disabled={board.disabled}
                    onClick={() => onSelectBoard(board.key)}
                  >
                    {board.actionLabel || 'Abrir'}
                  </button>
                  {board.disabled && <span className="helper-text">Configura este módulo para activarlo.</span>}
                </div>
              </article>
            ))}
          </div>
        </section>
      )}
    </section>
  );
}

export default ControlCenterOverview;
